import { useEffect, useState } from "react";
import { swr, invalidate } from "../outbox.js";
import { api, type Team } from "../api.js";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconChevron } from "../Icons.js";
import { Screen, Card, Row, Button, Field, Empty, Msg, Skeleton } from "../ui.js";

export function Teams({ clubId, canEdit }: { clubId: string; canEdit: boolean }) {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [short, setShort] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = async () => {
    const paint = (d: { teams: Team[] }) => setTeams(d.teams);
    const hit = await swr(`club:${clubId}`,
      () => api<{ teams: Team[] }>(`/api/v1/clubs/${clubId}`), paint);
    if (hit) paint(hit);
  };
  useEffect(() => { void load(); }, [clubId]);

  const add = async () => {
    const r = await api<{ error?: string }>(`/api/v1/clubs/${clubId}/teams`,
      { json: { name: name.trim(), shortName: short.trim() } });
    if (r.ok) { setName(""); setShort(""); setAdding(false); setMsg(null); invalidate(`club:${clubId}`); void load(); }
    else setMsg(r.data.error ?? "Could not create the team.");
  };

  return (
    <Screen title="Teams" sub={teams ? `${teams.length} in the club` : undefined}
      action={canEdit ? <Button onClick={() => setAdding((v) => !v)}><span className="flex items-center gap-1.5">{!adding && <IconPlus size={15} />}{adding ? "Cancel" : "New"}</span></Button> : undefined}>
      <Msg text={msg} kind="err" />

      {adding && (
        <div className="mb-4 space-y-3 rounded-xl border border-line bg-panel p-4">
          <Field label="Name" value={name} autoFocus placeholder="Team Altaf"
                 onChange={(e) => setName(e.target.value)} />
          <Field label="Short name" value={short} maxLength={4} placeholder="TA"
                 onChange={(e) => setShort(e.target.value.toUpperCase())} />
          <Button full onClick={add}>Create</Button>
        </div>
      )}

      {!teams ? <Card><Skeleton rows={4} /></Card>
      : teams.length === 0 ? <Card><Empty title="No teams yet">Create one, then add players to it.</Empty></Card>
      : (
        <Card>
          {teams.map((t) => (
            <Row key={t.id} onClick={() => navigate(`/team/${t.id}`)}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-line
                               font-num text-body font-semibold text-amber">{t.shortName}</span>
              <div className="min-w-0 flex-1"><b className="block truncate font-semibold">{t.name}</b></div>
              <span className="text-faint"><IconChevron /></span>
            </Row>
          ))}
        </Card>
      )}

          </Screen>
  );
}
